/**
 * Label-URL refresh — response contract for
 * `GET /api/seller-orders/[id]/label-url`.
 *
 * Tiny endpoint serving a single concern (PLAN §M.8 / §O.2.5): when the seller
 * clicks "Print label" and the Shippo CDN URL has expired (404 — empirically
 * ~30–90 d, unmeasured per §N.1), the client falls back to this endpoint, which
 * asks Shippo `GET /transactions/{id}` for the current URL and persists it.
 *
 * Why a contract type for such a small surface: it's parsed at the boundary
 * (component → builder → `safeParse` → typed value), and the array↔union pin
 * here will catch a future "we also return a qrCodeUrl" / "we now return a
 * cool-down hint" drift at `tsc` rather than at runtime.
 *
 * @see .tondocs/shippo/PLAN.md §M.8 — trip-wire
 * @see .tondocs/shippo/PLAN.md §O.2.5 — gate
 * @see app/api/seller-orders/[id]/label-url/route.ts
 */

import { z } from "zod";
import { pinned } from "./pin";

// ---------------------------------------------------------------------------
// Discriminator tuple — exhaustive list of every response `kind`
// ---------------------------------------------------------------------------

export const LABEL_URL_RESPONSE_KINDS = ["refreshed"] as const;

export type LabelUrlResponseKind = (typeof LABEL_URL_RESPONSE_KINDS)[number];

// ---------------------------------------------------------------------------
// Response variant — fresh URL pulled from Shippo
// ---------------------------------------------------------------------------

/**
 * The endpoint re-fetched the Shippo transaction and persisted a fresh
 * `external_label_url`. `qrCodeUrl` is included when Shippo's transaction
 * response carries one (mostly USPS / international); NULL otherwise.
 *
 * `refreshedAt` is the moment the route wrote the row, in ISO-8601 UTC. The
 * `label_purchased_at` timestamp is NOT touched — purchase time stays frozen,
 * only the URL is rotated.
 */
export interface LabelUrlRefreshedResponse {
  kind: "refreshed";
  labelUrl: string;
  qrCodeUrl: string | null;
  refreshedAt: string;
}

export type LabelUrlResponse = LabelUrlRefreshedResponse;

// Compile-time exhaustiveness pin — array↔union, matches the ready-to-ship
// sibling contract.
type _AssertLabelUrlKindsCoverUnion =
  (typeof LABEL_URL_RESPONSE_KINDS)[number] extends LabelUrlResponse["kind"]
    ? true
    : never;
type _AssertLabelUrlNoExtraKinds =
  LabelUrlResponse["kind"] extends (typeof LABEL_URL_RESPONSE_KINDS)[number]
    ? true
    : never;

const _pinLabelUrlKindsCoverUnion: _AssertLabelUrlKindsCoverUnion = true;
const _pinLabelUrlNoExtraKinds: _AssertLabelUrlNoExtraKinds = true;
void _pinLabelUrlKindsCoverUnion;
void _pinLabelUrlNoExtraKinds;

// ---------------------------------------------------------------------------
// Zod schema — pinned to interface
// ---------------------------------------------------------------------------

export const LabelUrlRefreshedResponseSchema = pinned<LabelUrlRefreshedResponse>()(
  z.object({
    kind: z.literal("refreshed"),
    labelUrl: z.string().min(1),
    qrCodeUrl: z.string().min(1).nullable(),
    refreshedAt: z.string().min(1),
  }),
);

export const LabelUrlResponseSchema = pinned<LabelUrlResponse>()(
  z.object({
    kind: z.literal("refreshed"),
    labelUrl: z.string().min(1),
    qrCodeUrl: z.string().min(1).nullable(),
    refreshedAt: z.string().min(1),
  }),
);
